import { Grid } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekday from 'dayjs/plugin/weekday';
import { useMemo, useState } from 'react';
import { CalendarDayCell } from './calendar-day-cell';
import { NavButton } from './nav-button';
import { WeekDays } from './week-days';

import React from 'react';
import { localize } from '/react4xp/mixins/helpers';

dayjs.extend(isoWeek);
dayjs.extend(weekday);

const { useBreakpoint } = Grid;

type Props = {
    selectedDay: Dayjs;
    selectDay: (day: Dayjs) => void;
    translations: Record<string, string>;
};

export function Calendar({ selectedDay, selectDay, translations }: Props) {
    const [cursor, setCursor] = useState<Dayjs>(selectedDay.startOf('day'));
    const { md } = useBreakpoint();

    const monthCalendarDays = useMemo(() => {
        const firstDay = cursor.startOf('month');
        const start = firstDay.startOf('week');

        const lastDay = cursor.endOf('month');
        const end = lastDay.endOf('week');

        const total = end.diff(start, 'day') + 1;
        const days: Dayjs[] = Array.from({ length: total }, (_, i) => start.add(i, 'day'));

        return days;
    }, [cursor]);

    const weekCalendarDays = useMemo(() => {
        const start = cursor.weekday(0);
        return Array.from({ length: 7 }, (_, i) => start.add(i, 'day'));
    }, [cursor]);

    const goPrevWeek = () => setCursor((c) => c.subtract(1, 'week'));
    const goNextWeek = () => setCursor((c) => c.add(1, 'week'));

    return (
        <div
            className="calendar"
            style={{
                backgroundImage: "url('/_/asset/isnareactcms/images/bg_calendar.svg')",
            }}
        >
            <div className="title">{localize('calendar.title', translations)}</div>

            {!md && (
                <div className="calendar_mobile__wrapper">
                    <NavButton
                        direction="left"
                        onClick={goPrevWeek}
                    />

                    <div className="calendar_mobile">
                        <WeekDays />
                        <div className="calendar_mobile__days">
                            {weekCalendarDays.map((day) => (
                                <CalendarDayCell
                                    key={day.format('YYYY-MM-DD')}
                                    isSelected={day.isSame(selectedDay, 'day')}
                                    day={day}
                                    onClick={() => selectDay(day)}
                                />
                            ))}
                        </div>
                    </div>

                    <NavButton
                        direction="right"
                        onClick={goNextWeek}
                    />
                </div>
            )}

            {md && (
                <div className="calendar_desktop__wrapper">
                    <div className="calendar_desktop">
                        <WeekDays />
                        <div className="calendar_desktop__days">
                            {monthCalendarDays.map((day) => (
                                <CalendarDayCell
                                    key={day.format('YYYY-MM-DD')}
                                    isSelected={day.isSame(selectedDay, 'day')}
                                    day={day}
                                    onClick={() => selectDay(day)}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
